import { z } from 'zod';

import { DiscountClass, DiscountKind, DiscountMethod } from '@nucleus-commerce/api-client/admin';
import { isMoney, type Money } from '@nucleus-commerce/core';

import { isCode } from './discount-rules';

const moneyValue = z.custom<Money>(isMoney).nullable();
const whole = z
  .string()
  .trim()
  .refine((v) => v === '' || /^[1-9]\d*$/.test(v), {
    error: 'discounts:validation.positiveWhole',
  });
const dateTime = z.string().refine((v) => v === '' || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v), {
  error: 'discounts:validation.dateTime',
});

export const DISCOUNT_SCOPES = ['order', 'products', 'collections'] as const;
export type DiscountScope = (typeof DISCOUNT_SCOPES)[number];

const PERCENT = /^\d{1,3}(\.\d{1,2})?$/;

/**
 * One schema for create and edit. Codes are part of the form only on create; on edit they are
 * managed through their own endpoint, so the kind/codes coupling is checked on create only.
 */
export function discountFormSchema(mode: 'create' | 'edit') {
  return z
    .object({
      title: z
        .string()
        .trim()
        .min(1, { error: 'discounts:validation.titleRequired' })
        .max(120, { error: 'discounts:validation.titleLength' }),
      kind: z.enum(DiscountKind),
      method: z.enum(DiscountMethod),
      percentage: z.string().trim(),
      amount: moneyValue,
      scope: z.enum(DISCOUNT_SCOPES),
      productIds: z.array(z.string()),
      collectionIds: z.array(z.string()),
      minSubtotal: moneyValue,
      minQuantity: whole,
      firstOrderOnly: z.boolean(),
      countries: z.array(z.string()),
      combinesWith: z.array(z.enum(DiscountClass)),
      usageLimitTotal: whole,
      usageLimitPerCustomer: whole,
      startsAt: dateTime,
      endsAt: dateTime,
      status: z.enum(['active', 'disabled']),
      codes: z.array(z.string()),
    })
    .superRefine((v, ctx) => {
      const issue = (path: string, message: string) =>
        ctx.addIssue({ code: 'custom', path: [path], message });

      if (v.method === 'percentage') {
        const value = Number(v.percentage);
        if (v.percentage === '') issue('percentage', 'discounts:validation.percentageRequired');
        else if (!PERCENT.test(v.percentage) || value < 0.01 || value > 100) {
          issue('percentage', 'discounts:validation.percentageRange');
        }
      }
      if (v.method === 'fixed_amount') {
        if (v.amount === null) issue('amount', 'discounts:validation.amountRequired');
        else if (v.amount.amount <= 0) issue('amount', 'discounts:validation.amountPositive');
      }
      if (v.scope === 'products' && v.productIds.length === 0) {
        issue('productIds', 'discounts:validation.productsRequired');
      }
      if (v.scope === 'collections' && v.collectionIds.length === 0) {
        issue('collectionIds', 'discounts:validation.collectionsRequired');
      }
      if (v.minSubtotal && v.minSubtotal.amount <= 0) {
        issue('minSubtotal', 'discounts:validation.amountPositive');
      }
      if (v.startsAt && v.endsAt && v.endsAt <= v.startsAt) {
        issue('endsAt', 'discounts:validation.endsBeforeStart');
      }
      if (mode === 'create') {
        if (v.kind === 'code' && v.codes.length === 0) {
          issue('codes', 'discounts:validation.codesRequired');
        }
        if (v.kind === 'automatic' && v.codes.length > 0) {
          issue('codes', 'discounts:validation.codesForbidden');
        }
        if (v.codes.some((code) => !isCode(code))) {
          issue('codes', 'discounts:validation.codeFormat');
        }
        if (v.codes.length > 100) issue('codes', 'discounts:validation.codesMax');
      }
    });
}

export type DiscountFormInput = z.input<ReturnType<typeof discountFormSchema>>;
export type DiscountFormOutput = z.output<ReturnType<typeof discountFormSchema>>;

export const generateCodesSchema = z.object({
  count: z.coerce
    .number<string | number>()
    .int({ error: 'discounts:validation.generateCount' })
    .min(1, { error: 'discounts:validation.generateCount' })
    .max(500, { error: 'discounts:validation.generateCount' }),
  prefix: z
    .string()
    .trim()
    .max(12, { error: 'discounts:validation.prefixFormat' })
    .regex(/^[A-Za-z0-9_-]*$/, { error: 'discounts:validation.prefixFormat' }),
});

export type GenerateCodesInput = z.input<typeof generateCodesSchema>;
export type GenerateCodesOutput = z.output<typeof generateCodesSchema>;
